import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plus, Search, Users, BarChart3,
  ArrowRight, RefreshCw, CheckCircle, AlertCircle, XCircle
} from 'lucide-react';
import { partnerApi, PortfolioCompany } from '../api/partner.api';
import AddCompanyModal from '../components/AddCompanyModal';
import PageInfoButton from '@/components/PageInfoButton';

function statusBadge(active: boolean) {
  return active
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3" />Active</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500"><XCircle className="w-3 h-3" />Inactive</span>;
}

export default function PartnerCompanies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<PortfolioCompany[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await partnerApi.getCompanies();
      setCompanies(res.companies);
    } finally {
      setLoading(false);
    }
  }

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.industry || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6 max-w-screen-xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">My Companies</h1><PageInfoButton /></div>
          <p className="text-sm text-slate-500 mt-1">{companies.length} companies in your portfolio</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Company
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search companies..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 bg-white rounded-3xl border border-slate-100">
          <Building2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">
            {search ? 'No companies match your search.' : 'No companies yet. Add your first client company to get started.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Company</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Industry</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />Users</span>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <span className="flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5" />Frameworks</span>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Avg Progress</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/partner/companies/${c.id}`)}>
                  <td className="px-4 py-3 text-left">
                    <div className="font-medium text-slate-900">{c.name}</div>
                    {c.legal_name && c.legal_name !== c.name && (
                      <div className="text-xs text-slate-400">{c.legal_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-left text-slate-600">{c.industry || '—'}</td>
                  <td className="px-4 py-3 text-left">{statusBadge(c.is_active)}</td>
                  <td className="px-4 py-3 text-left text-slate-700">{c.user_count}</td>
                  <td className="px-4 py-3 text-left text-slate-700">{c.framework_count}</td>
                  <td className="px-4 py-3 text-left">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${c.avg_completion >= 75 ? 'bg-emerald-500' : c.avg_completion >= 40 ? 'bg-amber-500' : 'bg-red-400'}`}
                          style={{ width: `${Math.min(100, c.avg_completion)}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-600">{Math.round(c.avg_completion)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-left">
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddCompanyModal
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}
